import React, { useState, useMemo, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  EditButton,
  ShowButton,
  TopToolbar,
  FunctionField,
  SearchInput,
} from 'react-admin';
import { Card, Typography, Box } from '@mui/material';
import {
  Add as AddIcon,
  DataObject as VariableIcon,
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import CustomDeleteButton from '../../components/CustomDeleteButton';
import CustomCreateButton from '@/components/CustomCreateButton';
import TableSelector from '@/components/AutocompleteSelector/TableSelector';

// Estilo para truncar texto
const textOverflowStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': {
    margin: 0,
  },
  '& svg': {
    fontSize: '16px',
  },
};

const VariableListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Variable'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

const variableFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar variables...'
    alwaysOn
    size='small'
    sx={{
      minWidth: 200,
      mb: 0.5,
    }}
  />,
];

export const VariablesList = () => {
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});

  // Estado para controlar paginación
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
  });

  // Handler
  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      console.log('🎯 Filtros cambiados:', selections);
      setFilterValues(selections);
      setPagination(prev => ({ ...prev, page: 1 }));
    },
    []
  );

  const filter = useMemo(() => {
    const filterObj: any = {};

    if (filterValues.plantId) {
      filterObj.plantId = filterValues.plantId;
    }

    if (filterValues.zoneId) {
      filterObj.zoneId = filterValues.zoneId;
    }

    if (filterValues.tableId) {
      filterObj.tableId = filterValues.tableId;
    }

    // Paginación para el backend
    filterObj.isPaginated = true;
    filterObj.page = pagination.page;
    filterObj.pageSize = pagination.perPage;

    console.log('🎯 Enviando parámetros al backend:', filterObj);

    return filterObj;
  }, [
    filterValues.plantId,
    filterValues.zoneId,
    filterValues.tableId,
    pagination.page,
    pagination.perPage,
  ]);

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <VariableIcon color='primary' />
          Gestión de Variables
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Gestiona las variables del sistema organizadas por tablas y zonas
        </Typography>
      </Box>

      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Filtros de Búsqueda
        </Typography>

        <HierarchicalFilter
          levels={['plant', 'zone', 'table']}
          onChange={handleFilterChange}
          size='medium'
          showTable={true}
        />

        {(filterValues.plantId ||
          filterValues.zoneId ||
          filterValues.tableId) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Selección actual:
              {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
              {filterValues.zoneName && ` → Zona: ${filterValues.zoneName}`}
              {filterValues.tableName && ` → Tabla: ${filterValues.tableName}`}
            </Typography>
            {filterValues.tableId && (
              <Typography variant='body2' color='success.main' sx={{ mt: 1 }}>
                ✓ Filtro aplicado: Mostrando variables de la tabla seleccionada
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <List
        title={false}
        actions={<VariableListActions />}
        filter={filter}
        filters={variableFilters}
        perPage={pagination.perPage}
        sort={{ field: 'variableName', order: 'ASC' }}
        exporter={false}
        sx={{
          '& .RaList-main': { mt: 0, p: 0 },
          '& .RaDatagrid-rowCell, & .RaDatagrid-headerCell': {
            padding: '6px 8px',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            pb: 1,
          }}
        >
          <Datagrid
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': { fontWeight: 'bold' },
              tableLayout: 'fixed',
              width: '100%',
            }}
          >
            {/* ID: */}
            <TextField
              source='id'
              label='ID'
              sx={{ minWidth: 35, maxWidth: 35, ...textOverflowStyle }}
            />

            <TextField
              source='variableName'
              label='Nombre de la Variable'
              sx={{
                maxWidth: 140,
                ...textOverflowStyle,
              }}
            />
            <TextField
              source='description'
              label='Descripción'
              sx={{
                maxWidth: 180,
                ...textOverflowStyle,
              }}
            />
            <TextField
              source='scadaReferenceName'
              label='Referencia SCADA'
              sx={{
                maxWidth: 110,
                ...textOverflowStyle,
              }}
            />

            <ReferenceField
              source='tableId'
              reference='tables'
              label='Tabla'
              link='show'
              sx={{ maxWidth: 110, ...textOverflowStyle }}
            >
              <FunctionField
                render={(record: any) => {
                  if (!record) return 'Sin tabla';
                  return (
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={0.5}
                      sx={textOverflowStyle}
                    >
                      <TableIcon color='action' fontSize='small' />
                      <span>
                        {record.tableName || record.name || 'Tabla sin nombre'}
                      </span>
                    </Box>
                  );
                }}
              />
            </ReferenceField>

            {/* Zona */}
            <FunctionField
              label='Zona'
              render={(record: any) => {
                if (!record || !record.tableId) return 'Sin zona';
                return (
                  <ReferenceField
                    record={{ id: record.tableId }}
                    source='id'
                    reference='tables'
                    link={'show'}
                    sx={{ ...textOverflowStyle }}
                  >
                    <ReferenceField
                      source='zoneId'
                      reference='zones'
                      link='show'
                    >
                      <FunctionField
                        render={(zoneRecord: any) => {
                          if (!zoneRecord) return 'Sin zona';
                          return (
                            <Box
                              display='flex'
                              alignItems='center'
                              gap={0.5}
                              sx={textOverflowStyle}
                            >
                              <ZoneIcon color='action' fontSize='small' />
                              <span>
                                {zoneRecord.zoneName ||
                                  zoneRecord.name ||
                                  'Zona sin nombre'}
                              </span>
                            </Box>
                          );
                        }}
                      />
                    </ReferenceField>
                  </ReferenceField>
                );
              }}
              sortable={false}
              sx={{ maxWidth: 90, ...textOverflowStyle }}
            />

            <FunctionField
              label='Acciones'
              render={(record: any) => (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    minWidth: '90px',
                  }}
                >
                  <ShowButton sx={buttonStyle} label={''} />
                  <EditButton sx={buttonStyle} label={''} />
                  <CustomDeleteButton
                    record={record}
                    resource='variables'
                    nameField='variableName'
                    sx={buttonStyle}
                    label={false}
                  />
                </Box>
              )}
              sortable={false}
              sx={{
                minWidth: '95px',
                maxWidth: '95px',
                textAlign: 'center',
                padding: '2px 1px !important',
              }}
            />
          </Datagrid>
        </Box>
      </List>
    </Card>
  );
};

export default VariablesList;
